import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';

import {
  useGoogleMapsStateUpdater,
  useGoogleMapsStateValue,
  withGoogleMapsState,
} from '@atom/components/common/map/hooks/googleMapsStateHook';
import Map from '@atom/components/common/map/Map';
import MapMarkers from '@atom/components/common/map/MapMarkers';
import MapPinMarker from '@atom/components/common/map/markers/MapPinMarker';
import { INITIAL_LAYERS } from '@atom/components/mapPortal/hooks/layersHook';
import { useMapData } from '@atom/components/mapPortal/hooks/mapDataHook';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Progress } from '@atom/mui';
import {
  defaultClusterDensitySelector,
  getLoggedInUserMapLocationSelector,
} from '@atom/selectors/mapSelectors';
import { MapParams } from '@atom/types/map';
import {
  buildMapDataRequest,
  getGeoJsonFromMapBounds,
  getMapCenterAndZoomParams,
  urlValueToLatLng,
} from '@atom/utilities/mapUtilities';

const TeamMapView = () => {
  const currentUserLocation = useSelector(getLoggedInUserMapLocationSelector);
  const clusterDensity = useSelector(defaultClusterDensitySelector);

  const dispatch = useGoogleMapsStateUpdater();
  const { isLoaded } = useGoogleMapsStateValue();
  const preferences = usePreferences();

  const [map, setMap] = useState<google.maps.Map>(null);
  const [coordinates, setCoordinates] = useState<string>(null);

  useEffect(() => {
    dispatch({
      type: 'SET',
      payload: {
        key: 'grab',
        data: false,
      },
    });
  }, []);

  const [{ zoom, center, searchPoint }, { navigate, updateSearchParams }] =
    useNavigation({
      zoom: SearchParamType.NUMBER,
      center: SearchParamType.STRING,
      searchPoint: SearchParamType.STRING,
    });
  const navigateToDetailPage = (id: string) => {
    navigate(`/team/${id}`);
  };

  const onLoad = useCallback(setMap, []);

  const layers = {
    ...INITIAL_LAYERS,
    assets: false,
    work: false,
    kmlLayers: false,
    workStatusIds: new Set([]),
    clusterDensity,
  };

  const url = buildMapDataRequest(layers, {
    coordinates,
    zoom,
  });

  const [mapData, loading] = useMapData(url);

  const onIdle = useCallback(() => {
    try {
      const existingMapParams: MapParams = {
        center,
        zoom,
        searchPoint,
      };

      const mapParams = getMapCenterAndZoomParams(
        map,
        existingMapParams,
        preferences,
        currentUserLocation,
      );

      if (!R.equals(existingMapParams, mapParams) || !coordinates) {
        updateSearchParams({
          zoom: mapParams.zoom,
          center: mapParams.center,
        });
        R.pipe(
          getGeoJsonFromMapBounds,
          R.prop('coordinates'),
          JSON.stringify,
          setCoordinates,
        )(map);
      }
    } catch {
      updateSearchParams({ zoom: null, center: null });
    }
  }, [
    map,
    zoom,
    center,
    searchPoint,
    coordinates,
    preferences,
    updateSearchParams,
  ]);

  return !isLoaded ? (
    <Progress style={{ height: '100%' }} />
  ) : (
    <Map
      onIdle={onIdle}
      onLoad={onLoad}
      center={center && urlValueToLatLng(center)}
      zoom={zoom}
      loading={loading}
      mapStyleControl
      mapSearchBox
    >
      <MapMarkers data={mapData} onClick={navigateToDetailPage} />
      {searchPoint && <MapPinMarker center={searchPoint} />}
    </Map>
  );
};

export default withGoogleMapsState(TeamMapView);
